import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { userAPI } from "../../services/api";
import Toast from "../../components/Toast";
import { Loader2 } from "lucide-react";
import FlowFooter from "../../components/FlowFooter";

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_DURATION_MS = 2 * 60 * 1000;

export default function PostAccountAggregatorDigitapp() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "warning" });

  const hideToast = () => setToast({ show: false, message: "", type: "warning" });
  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;
    const startTime = Date.now();

    const goToUploadBankStatement = () => {
      if (isMounted) navigate("/apply/upload-bank-statement");
    };

    const poll = async () => {
      try {
        const response = await userAPI.checkAAStatus({
          request_id: null,
          txn_id: null,
          status: null,
        });

        if (!isMounted) return;

        if (response?.is_aa_failed || response?.status === "Failure") {
          goToUploadBankStatement();
          return;
        }

        if (response?.status === "in-progress") {
          if (Date.now() - startTime >= MAX_POLL_DURATION_MS) {
            goToUploadBankStatement();
            return;
          }
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
          return;
        }

        if (response?.is_hard_reject) {
          navigate("/apply/rejected");
        } else if (response?.is_bre_reject) {
          navigate("/apply/thank-you");
        } else {
          navigate("/apply/loan-amount");
        }
      } catch (error) {
        if (isMounted) {
          showToast(error.message || "Something went wrong. Please try again.");
          goToUploadBankStatement();
        }
      }
    };

    setIsLoading(false);
    setIsCheckingStatus(true);
    poll();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col justify-center items-center pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 min-h-0">
        <div className="max-w-md w-full mx-auto">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl shadow-[#FF6B35]/10 text-center">
            {(isLoading || isCheckingStatus) && (
              <div className="py-6 sm:py-8">
                <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 text-[#FF6B35] animate-spin" />
                <h2 className="text-lg sm:text-xl font-bold text-[#1E3A8A] mb-2">
                  {isCheckingStatus ? "Analyzing your bank statements" : "Processing..."}
                </h2>
                <p className="text-xs sm:text-sm text-[#64748B]">
                  {isCheckingStatus
                    ? "Please wait while we verify your bank account data..."
                    : "Setting things up for you..."}
                </p>
                <div className="flex items-center justify-center gap-2 mt-6">
                  <div className="w-2.5 h-2.5 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-2.5 h-2.5 bg-[#FF6B35]/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-2.5 h-2.5 bg-[#FF6B35]/30 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <FlowFooter />
    </div>
  );
}
